"use client"

import { useActionState } from "react"
import { lookupEmailAddress } from "../actions/lookupEmailAddress"
import {
    isLookupEmailAddressFoundState,
    isLookupEmailAddressNotFoundErrorState,
    isLookupEmailAddressOrgIssueErrorState,
    LookupEmailAddressOrgIssueErrorState,
} from "../actions/LookupEmailAddressState"
import { Button } from "@ui/button"
import { Text } from "@ui/text"
import { ErrorMessage, Field } from "@ui/fieldset"
import { motion } from "motion/react"
import { EmailField } from "../../register/components/IndividualSubForm"
import { redirect } from "next/navigation"
import { ReturnToForwardHiddenInput } from "./ReturnToForwardHiddenInput"

function OrgIssue({ formState }: { formState: LookupEmailAddressOrgIssueErrorState }) {
    const errorMessage = formState.status === "no-org" ? "No organization found" : "Multiple organizations found"

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
        >
            <div className="space-y-4">
                <Text>
                    We're sorry, but there seems to be an issue with your organization's account. Unfortunately, this
                    error cannot be resolved on your end.
                </Text>
                <Text>Please contact our support team for further assistance.</Text>
                <Button
                    href={`mailto:support@reseaerchwiseai.com?subject=${encodeURIComponent(
                        "Error while Signing in to ResearchWiseAI"
                    )}&body=${encodeURIComponent(
                        `Hello Support Team,\n\nWhile logging in to ResearchWise AI, I got the following error:\n - ${errorMessage}\n\nEmail: ${formState.email}\n\nThank you,`
                    )}`}
                >
                    Contact Support
                </Button>
            </div>
        </motion.div>
    )
}

function AccountNotFound() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
        >
            <div className="space-y-4">
                <Text>We couldn’t find an account with that email.</Text>
                <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => window.location.reload()}>Try Again</Button>
                    <Button href="/register" color="primary">
                        Sign Up
                    </Button>
                </div>
            </div>
        </motion.div>
    )
}

export function EmailLookupForm() {
    const [formState, lookup, pending] = useActionState(lookupEmailAddress, { status: "fresh" })

    if (isLookupEmailAddressOrgIssueErrorState(formState)) {
        return <OrgIssue formState={formState} />
    }

    if (isLookupEmailAddressNotFoundErrorState(formState)) {
        return <AccountNotFound />
    }

    if (isLookupEmailAddressFoundState(formState)) {
        return redirect(formState.url)
    }

    return (
        <form action={lookup}>
            <ReturnToForwardHiddenInput />
            {formState.status === "error" && Array.isArray(formState.errors?.general) && (
                <>
                    {formState.errors.general.map((error) => (
                        <Field>
                            <ErrorMessage key={error}>{error}</ErrorMessage>
                        </Field>
                    ))}
                </>
            )}
            <EmailField formState={"errors" in formState ? formState : {}} />
            <Button disabled={pending} type="submit" color="primary" className="mt-8 w-full">
                {pending ? "Finding your account..." : "Sign in"}
            </Button>
        </form>
    )
}
